/**
 * Tournament TypeScript types for Admin Frontend
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * Challenge mode: determines whether it's a simple one-time match or knockout tournament
 */
export enum ChallengeMode {
  OneTimeMatching = 0,
  KnockoutTournament = 1,
}

/**
 * Tournament lifecycle status
 */
export enum TournamentStatus {
  Created = 'Created',
  InProgress = 'InProgress',
  SemiFinals = 'SemiFinals',
  Finals = 'Finals',
  Completed = 'Completed',
  Scheduled = 'Scheduled',
}

/**
 * Individual match status within a tournament
 */
export enum MatchStatus {
  Bye = 'Bye',
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Forfeit = 'Forfeit',
}

/**
 * Type of round in the tournament bracket.
 * Supports tournaments with up to 64 participants (6 rounds max).
 */
export enum RoundType {
  RoundOf64 = 'RoundOf64',
  RoundOf32 = 'RoundOf32',
  RoundOf16 = 'RoundOf16',
  QuarterFinals = 'QuarterFinals',
  SemiFinals = 'SemiFinals',
  Final = 'Final',
  ThirdPlace = 'ThirdPlace',
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Tournament-specific configuration for knockout mode
 */
export interface TournamentConfig {
  thirdPlaceMatchEnabled: boolean;
  points1stPlace: number;
  points2ndPlace: number;
  points3rdPlace: number;
  scheduledStartDate?: string | null;
}

/**
 * Default tournament configuration values
 */
export const DEFAULT_TOURNAMENT_CONFIG: TournamentConfig = {
  thirdPlaceMatchEnabled: false,
  points1stPlace: 100,
  points2ndPlace: 50,
  points3rdPlace: 25,
  scheduledStartDate: null,
};

// ============================================================================
// Response/DTO Types
// ============================================================================

/**
 * Participant information for tournament display
 */
export interface TournamentParticipant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  tournamentWins?: number;
  tournamentTitle?: string;
  tournamentBadgeKey?: string;
}

/**
 * Tournament match information for bracket display
 */
export interface TournamentMatchDto {
  id: string;
  bracketPosition: number;
  status: MatchStatus | string;
  isBye: boolean;
  participant1: TournamentParticipant | null;
  participant2: TournamentParticipant | null;
  winner: TournamentParticipant | null;
  loser: TournamentParticipant | null;
  participant1Score: number | null;
  participant2Score: number | null;
  challengesMatchId: string | null;
  nextMatchId: string | null;
  dateCompleted: string | null;
}

/**
 * Tournament round information for bracket display
 */
export interface TournamentRoundDto {
  id: string;
  roundNumber: number;
  roundType: RoundType | string;
  displayName: string;
  status: string;
  matchCount: number;
  matches: TournamentMatchDto[];
}

/**
 * Full tournament details
 */
export interface TournamentDto {
  id: string;
  title: string;
  mode: string;
  status: TournamentStatus | string;
  bracketSize: number;
  participantCount: number;
  byeCount: number;
  thirdPlaceMatchEnabled: boolean;
  points1stPlace: number;
  points2ndPlace: number;
  points3rdPlace: number;
  examTermId: string;
  dateCreated: string;
  dateCompleted: string | null;
  scheduledStartDate: string | null;
  isScheduled: boolean;
  currentRound: TournamentRoundDto | null;
  participants: TournamentParticipant[];
  winner: TournamentParticipant | null;
  secondPlace: TournamentParticipant | null;
  thirdPlace: TournamentParticipant | null;
}

/**
 * Placement points configuration
 */
export interface PlacementPoints {
  firstPlace: number;
  secondPlace: number;
  thirdPlace: number;
}

/**
 * Full bracket visualization data
 */
export interface BracketData {
  tournamentId: string;
  title: string;
  status: TournamentStatus | string;
  bracketSize: number;
  participantCount: number;
  byeCount: number;
  totalRounds: number;
  thirdPlaceMatchEnabled: boolean;
  rounds: TournamentRoundDto[];
  thirdPlaceRound: TournamentRoundDto | null;
  winner: TournamentParticipant | null;
  placementPoints: PlacementPoints;
  placements?: {
    first?: TournamentParticipant | null;
    second?: TournamentParticipant | null;
    third?: TournamentParticipant | null;
  };
}

/**
 * Round summary for creation response
 */
export interface RoundSummary {
  roundNumber: number;
  roundType: string;
  matchCount: number;
  byeMatchCount: number;
}

/**
 * Response after creating a tournament
 */
export interface CreateTournamentResponse {
  tournamentId: string;
  title: string;
  bracketSize: number;
  byeCount: number;
  totalRounds: number;
  rounds: RoundSummary[];
  examTermId: string;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Minimum participants for knockout tournament
 */
export const MIN_KNOCKOUT_PARTICIPANTS = 4;

/**
 * Maximum participants for knockout tournament (multi-phase needed for larger)
 */
export const MAX_KNOCKOUT_PARTICIPANTS = 64;

/**
 * Get display name for a round type
 */
export function getRoundDisplayName(roundType: RoundType | string): string {
  switch (roundType) {
    case RoundType.RoundOf64:
      return 'Round of 64';
    case RoundType.RoundOf32:
      return 'Round of 32';
    case RoundType.RoundOf16:
      return 'Round of 16';
    case RoundType.QuarterFinals:
      return 'Quarter-Finals';
    case RoundType.SemiFinals:
      return 'Semi-Finals';
    case RoundType.Final:
      return 'Final';
    case RoundType.ThirdPlace:
      return 'Third Place Match';
    default:
      return roundType;
  }
}

/**
 * Get the round type for a given bracket size and round number (1-based)
 */
export function getRoundTypeForBracket(_bracketSize: number, roundNumber: number, totalRounds: number): RoundType {
  const roundsFromFinal = totalRounds - roundNumber;
  
  switch (roundsFromFinal) {
    case 0: return RoundType.Final;
    case 1: return RoundType.SemiFinals;
    case 2: return RoundType.QuarterFinals;
    case 3: return RoundType.RoundOf16;
    case 4: return RoundType.RoundOf32;
    case 5: return RoundType.RoundOf64;
    default: return RoundType.RoundOf64;
  }
}

/**
 * Calculate bracket size for a given participant count
 */
export function calculateBracketSize(participantCount: number): number {
  if (participantCount < 4) return 4;
  let size = 4;
  while (size < participantCount) {
    size *= 2;
  }
  return size;
}

/**
 * Calculate number of BYEs for a given participant count
 */
export function calculateByeCount(participantCount: number): number {
  const bracketSize = calculateBracketSize(participantCount);
  return bracketSize - participantCount;
}
